/**
 * MUTAGIM.SHOP — Admin Newsletter Page (Phase 19)
 * Gestion des campagnes newsletter et des listes de diffusion
 */

import { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Badge } from '@/components/ui/badge';
import { Switch } from '@/components/ui/switch';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '@/components/ui/dialog';
import { toast } from 'sonner';
import {
  Mail, Users, Send, Calendar, BarChart3,
  Plus, Eye, Clock, CheckCircle, XCircle, Globe
} from 'lucide-react';
import ReactQuill from 'react-quill';
import 'react-quill/dist/quill.snow.css';
import { AdminPageWrapper } from '@/components/admin/AdminPageWrapper';
import { useAdminTheme } from '@/contexts/AdminThemeContext';
import { cn } from '@/lib/utils';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export function AdminNewsletterPage() {
  const navigate = useNavigate();
  const { isLight } = useAdminTheme();
  const [isLoading, setIsLoading] = useState(true);
  const [token, setToken] = useState('');
  const [activeTab, setActiveTab] = useState('campaigns');
  
  // Lists state
  const [lists, setLists] = useState({ lists: [], by_language: {}, by_country: {} });
  
  // Campaigns state
  const [campaigns, setCampaigns] = useState([]);
  
  // New campaign form
  const [showNewCampaign, setShowNewCampaign] = useState(false);
  const [campaignForm, setCampaignForm] = useState({
    name: '',
    subject: { fr: '', en: '', he: '' },
    content: { fr: '', en: '', he: '' },
    target_list: 'all',
    target_filter: null,
    scheduled_at: null,
  });
  const [contentLang, setContentLang] = useState('fr');
  const [isSending, setIsSending] = useState(false);
  
  // Settings
  const [doubleOptIn, setDoubleOptIn] = useState(true);

  useEffect(() => {
    const storedToken = localStorage.getItem('admin_token');
    if (storedToken) {
      setToken(storedToken);
      fetchData(storedToken);
    } else {
      setIsLoading(false);
    }
  }, []);

  const fetchData = async (authToken) => {
    try {
      const [listsRes, campaignsRes] = await Promise.all([
        fetch(`${API_URL}/api/engagement/newsletter/lists`, {
          headers: { Authorization: `Bearer ${authToken}` }
        }),
        fetch(`${API_URL}/api/engagement/newsletter/campaigns`, {
          headers: { Authorization: `Bearer ${authToken}` }
        })
      ]);
      
      if (listsRes.ok) {
        setLists(await listsRes.json());
      }
      if (campaignsRes.ok) {
        const data = await campaignsRes.json();
        setCampaigns(data.campaigns || []);
      }
    } catch (error) {
      console.error('Fetch error:', error);
    } finally {
      setIsLoading(false);
    }
  };

  const handleCreateCampaign = async (sendNow = true) => {
    if (!campaignForm.name || !campaignForm.subject.fr) {
      toast.error('Veuillez remplir le nom et le sujet');
      return;
    }
    
    setIsSending(true);
    try {
      const payload = {
        ...campaignForm,
        scheduled_at: sendNow ? null : campaignForm.scheduled_at
      };
      
      const res = await fetch(`${API_URL}/api/engagement/newsletter/campaigns`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`
        },
        body: JSON.stringify(payload)
      });
      
      if (res.ok) {
        toast.success(sendNow ? 'Campagne envoyée !' : 'Campagne planifiée');
        setShowNewCampaign(false);
        setCampaignForm({
          name: '',
          subject: { fr: '', en: '', he: '' },
          content: { fr: '', en: '', he: '' },
          target_list: 'all',
          target_filter: null,
          scheduled_at: null,
        });
        fetchData(token);
      } else {
        toast.error('Erreur lors de la création');
      }
    } catch (error) {
      toast.error('Erreur réseau');
    } finally {
      setIsSending(false);
    }
  };

  if (isLoading) {
    return (
      <AdminPageWrapper title="Newsletter">
        <div className="flex items-center justify-center h-64">
          <div className="animate-spin h-8 w-8 border-4 border-mutagim-red border-t-transparent rounded-full" />
        </div>
      </AdminPageWrapper>
    );
  }

  return (
    <AdminPageWrapper title="Newsletter" subtitle="Gérer les campagnes email">
      <div className="max-w-7xl mx-auto" data-testid="admin-newsletter-page">
        <div className="flex justify-end mb-6">
          <Button onClick={() => setShowNewCampaign(true)} data-testid="new-campaign-btn">
            <Plus className="h-4 w-4 mr-2" />
            Nouvelle campagne
          </Button>
        </div>

        <Tabs value={activeTab} onValueChange={setActiveTab}>
          <TabsList className={cn("mb-6", isLight ? "bg-gray-200" : "")}>
            <TabsTrigger value="campaigns">Campagnes</TabsTrigger>
            <TabsTrigger value="lists">Listes</TabsTrigger>
            <TabsTrigger value="settings">Paramètres</TabsTrigger>
          </TabsList>

          {/* Campagnes Tab */}
          <TabsContent value="campaigns">
            <div className="space-y-4">
              {campaigns.length === 0 ? (
                <Card>
                  <CardContent className="py-12 text-center">
                    <Mail className="h-12 w-12 mx-auto text-white mb-4" />
                    <p className="text-gray-500">Aucune campagne pour l'instant</p>
                    <Button 
                      className="mt-4" 
                      onClick={() => setShowNewCampaign(true)}
                    >
                      Créer ma première campagne
                    </Button>
                  </CardContent>
                </Card>
              ) : (
                campaigns.map(campaign => (
                  <Card key={campaign.id}>
                    <CardContent className="py-4">
                      <div className="flex items-center justify-between">
                        <div>
                          <div className="flex items-center gap-2">
                            <h3 className="font-semibold">{campaign.name}</h3>
                            <Badge variant={
                              campaign.status === 'sent' ? 'default' :
                              campaign.status === 'scheduled' ? 'secondary' : 'outline'
                            }>
                              {campaign.status === 'sent' ? 'Envoyé' :
                               campaign.status === 'scheduled' ? 'Planifié' : campaign.status}
                            </Badge>
                          </div>
                          <p className="text-sm text-gray-500 mt-1">
                            {campaign.subject?.fr}
                          </p>
                        </div>
                        
                        <div className="flex items-center gap-6 text-sm text-gray-500">
                          <div className="text-center">
                            <div className="font-semibold text-gray-900">{campaign.sent_count}</div>
                            <div>Envoyés</div>
                          </div>
                          <div className="text-center">
                            <div className="font-semibold text-gray-900">{campaign.open_rate}%</div>
                            <div>Ouvertures</div>
                          </div>
                          <div className="text-center">
                            <div className="font-semibold text-gray-900">{campaign.click_rate}%</div>
                            <div>Clics</div>
                          </div>
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                ))
              )}
            </div>
          </TabsContent>

          {/* Listes Tab */}
          <TabsContent value="lists">
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
              {lists.lists.map(list => (
                <Card key={list.id}>
                  <CardContent className="py-4">
                    <div className="flex items-center justify-between">
                      <div>
                        <p className="font-medium">{list.name}</p>
                        <p className="text-2xl font-bold text-mutagim-red">{list.count}</p>
                      </div>
                      <Users className="h-8 w-8 text-white" />
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
            
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <Card>
                <CardHeader>
                  <CardTitle className="text-lg flex items-center gap-2">
                    <Globe className="h-5 w-5" />
                    Par langue
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="space-y-2">
                    {Object.entries(lists.by_language).map(([lang, count]) => (
                      <div key={lang} className="flex justify-between">
                        <span className="text-gray-600">{lang === 'fr' ? 'Français' : lang === 'en' ? 'English' : lang === 'he' ? 'עברית' : lang}</span>
                        <span className="font-medium">{count}</span>
                      </div>
                    ))}
                  </div>
                </CardContent>
              </Card>
              
              <Card>
                <CardHeader>
                  <CardTitle className="text-lg flex items-center gap-2">
                    <Globe className="h-5 w-5" />
                    Par pays
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="space-y-2">
                    {Object.entries(lists.by_country).slice(0, 5).map(([country, count]) => (
                      <div key={country} className="flex justify-between">
                        <span className="text-gray-600">{country || 'Non défini'}</span>
                        <span className="font-medium">{count}</span>
                      </div>
                    ))}
                  </div>
                </CardContent>
              </Card>
            </div>
          </TabsContent>

          {/* Settings Tab */}
          <TabsContent value="settings">
            <Card>
              <CardHeader>
                <CardTitle>Paramètres Newsletter</CardTitle>
              </CardHeader>
              <CardContent className="space-y-6">
                <div className="flex items-center justify-between">
                  <div>
                    <Label className="text-base">Double Opt-In</Label>
                    <p className="text-sm text-gray-500">
                      Envoyer un email de confirmation avant d'activer l'inscription
                    </p>
                  </div>
                  <Switch
                    checked={doubleOptIn}
                    onCheckedChange={setDoubleOptIn}
                    data-testid="double-optin-switch"
                  />
                </div>
              </CardContent>
            </Card>
          </TabsContent>
        </Tabs>
      </div>

      {/* New Campaign Dialog */}
      <Dialog open={showNewCampaign} onOpenChange={setShowNewCampaign}>
        <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Nouvelle Campagne</DialogTitle>
          </DialogHeader>
          
          <div className="space-y-6 py-4">
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Nom de la campagne</Label>
                <Input
                  value={campaignForm.name}
                  onChange={e => setCampaignForm(prev => ({ ...prev, name: e.target.value }))}
                  placeholder="Ex: Soldes d'été 2026"
                  data-testid="campaign-name-input"
                />
              </div>
              
              <div>
                <Label>Liste cible</Label>
                <Select
                  value={campaignForm.target_list}
                  onValueChange={v => setCampaignForm(prev => ({ ...prev, target_list: v }))}
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">Tous les inscrits</SelectItem>
                    <SelectItem value="active_buyers">Acheteurs actifs (30j)</SelectItem>
                    <SelectItem value="crew">Membres Crew</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
            
            <div>
              <div className="flex items-center justify-between mb-2">
                <Label>Contenu par langue</Label>
                <div className="flex gap-1">
                  {['fr', 'en', 'he'].map(lang => (
                    <Button
                      key={lang}
                      variant={contentLang === lang ? 'default' : 'outline'}
                      size="sm"
                      onClick={() => setContentLang(lang)}
                    >
                      {lang.toUpperCase()}
                    </Button>
                  ))}
                </div>
              </div>
              
              <Input
                value={campaignForm.subject[contentLang] || ''}
                onChange={e => setCampaignForm(prev => ({
                  ...prev,
                  subject: { ...prev.subject, [contentLang]: e.target.value }
                }))}
                placeholder="Sujet de l'email"
                className="mb-2"
                data-testid="campaign-subject-input"
              />
              
              <ReactQuill
                theme="snow"
                value={campaignForm.content[contentLang] || ''}
                onChange={value => setCampaignForm(prev => ({
                  ...prev,
                  content: { ...prev.content, [contentLang]: value }
                }))}
                placeholder="Contenu de l'email..."
                className="bg-white"
              />
            </div>
            
            <div className="flex justify-end gap-2 pt-4 border-t">
              <Button variant="outline" onClick={() => setShowNewCampaign(false)}>
                Annuler
              </Button>
              <Button
                onClick={() => handleCreateCampaign(true)}
                disabled={isSending}
                data-testid="send-campaign-btn"
              >
                {isSending ? 'Envoi...' : 'Envoyer maintenant'}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </AdminPageWrapper>
  );
}

export default AdminNewsletterPage;
